/**
 * The Change / Select subscription plan dialog, lifted out of the billing page's
 * "Manage" button for DEV-2403, so the Custom Rules drawer's plan gate can open it
 * without losing the half-written rule. `open` / `onClose` are props now; nothing
 * else changed.
 *
 * Renders IN PLACE rather than through a portal (the DS Modal does not portal),
 * which is what lets the drawer mount it inside its own z-[9999] root.
 */
import { ChangePlanModal, type ChangePlanPlan } from '@clickguard/design-system'
import { DiscountInfoDTO, PricingTierPriceInfoDTO } from 'api-models'
import { useStores } from 'hooks'
import { useChangeSubscription } from 'hooks/useChangeSubscription'
import moment from 'moment'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { blackFridayOfferQuery } from 'services/user/black-friday-offer'
import { useUserStore } from 'store'
import { formatCurrency } from 'utils'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import {
	BillingPeriod,
	buildTierPlans,
	CONFIG_FIELDS_FIX,
	configFieldsFixCss,
	couponErrorMessage,
	CYCLES,
	FEATURES,
	getBasePrice,
	getPrices,
	getSubscriptionBillingPeriod,
	PERIOD_TO_COUNT,
	PERIOD_TO_INTERVAL
} from './plan-modal-helpers'

export interface ChangePlanDialogProps {
	open: boolean
	onClose: () => void
	/** Where the upgrade was offered, for the confirmation event. */
	path: PostHogPath
}

export const ChangePlanDialog = ({
	open,
	onClose,
	path
}: ChangePlanDialogProps) => {
	const { accountStore, billingStore } = useStores()
	const { account } = accountStore
	const type = account?.billing?.subscription?.type
	const posthog = usePostHog()
	const trackPostHogEvent = useTrackPostHogEvent()
	const { error, clearError, isBusy, submitLabel, changePlan } =
		useChangeSubscription()

	const [cycle, setCycle] = useState<BillingPeriod>('yearly')
	const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
	const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null)
	const [customSpend, setCustomSpend] = useState('')
	const [couponCode, setCouponCode] = useState('')
	const [coupon, setCoupon] = useState<DiscountInfoDTO | null>(null)
	const [couponError, setCouponError] = useState<string | undefined>(undefined)

	const plans = billingStore.plans
	const userId = useUserStore((state) => state.user?.id)
	const { data: campaign, isLoading: campaignLoading } = useQuery(
		['black-friday-offer', account?.id, userId],
		({ signal }) => blackFridayOfferQuery(account!.id, signal),
		{
			enabled:
				open && !!account?.id && !!userId && account.id !== 'workspace-demo',
			staleTime: 15000,
			retry: 1
		}
	)
	const campaignQuote = campaign?.offer.quote
	const discountEnd = campaignQuote
		? moment(campaignQuote.switch_at).add(1, 'year')
		: null
	const campaignActive =
		campaign?.offer.claim?.status === 'switched' &&
		!!discountEnd &&
		moment(campaign.offer.server_now).isBefore(discountEnd) &&
		moment().isBefore(discountEnd)
	const campaignPending = ['claiming', 'claimed'].includes(
		campaign?.offer.claim?.status || ''
	)

	useEffect(() => {
		billingStore.initBilling()
	}, [])

	// Surface submit failures — the DS modal has no inline error slot.
	useEffect(() => {
		if (error) toast.error(error)
	}, [error])

	// Each opening starts from the subscription's own billing period with nothing carried
	// over from the last visit — a failed attempt, a cycle the user was only browsing, or a
	// coupon they did not submit. The trigger no longer owns this instance, so the reset
	// belongs here, where every entry point gets it. DEV-2459.
	useEffect(() => {
		if (!open) return
		clearError()
		setCycle(getSubscriptionBillingPeriod(account?.billing?.subscription))
		setSelectedPlanId(null)
		setSelectedPriceId(null)
		setCoupon(null)
		setCouponCode('')
		setCouponError(undefined)
	}, [open])

	const billingDate = useMemo(
		() =>
			moment(
				account?.billing?.subscription?.next_billing_date ||
					account?.billing?.subscription?.ends_at ||
					account?.billing?.subscription?.ended_at
			).format('LL'),
		[account]
	)

	const isCustom = selectedPlanId === 'custom'
	const selectedPlan = plans.find((p) => p.tier === selectedPlanId) || null
	const selectedPrice: PricingTierPriceInfoDTO | null = selectedPlan
		? selectedPlan.prices.find((p) => p.price_id === selectedPriceId) ||
		  getBasePrice(selectedPlan, cycle)
		: null

	const selectPlan = (planId: string, period: BillingPeriod = cycle) => {
		posthog.capture(`click-on-${planId}-plan`)
		if (planId === 'custom') {
			setSelectedPlanId('custom')
			setSelectedPriceId(null)
			return
		}
		const plan = plans.find((p) => p.tier === planId)
		if (!plan) return
		setSelectedPlanId(planId)
		setSelectedPriceId(getBasePrice(plan, period)?.price_id ?? null)
	}

	// Default-select the recommended plan once plans load (Figma opens with
	// Standard expanded).
	// The period comes from the subscription rather than `cycle`, which still holds the previous
	// value while the reset above is committing.
	useEffect(() => {
		if (!open || selectedPlanId || !plans.length) return
		const recommended = plans.find((p) => p.tier === 'standard') || plans[0]
		selectPlan(
			recommended.tier,
			getSubscriptionBillingPeriod(account?.billing?.subscription)
		)
	}, [open, plans, selectedPlanId])

	const changeCycle = (next: BillingPeriod) => {
		posthog.capture(`click-on-${next}-plan`)
		setCycle(next)
		if (isCustom || !selectedPlan) return
		// Keep the same ad-spend tier across the period switch when possible.
		const current = selectedPlan.prices.find(
			(p) => p.price_id === selectedPriceId
		)
		const matched =
			getPrices(selectedPlan, next).find((p) => p.spend === current?.spend) ||
			getBasePrice(selectedPlan, next)
		setSelectedPriceId(matched?.price_id ?? null)
	}

	const claimCoupon = async (code: string) => {
		if (!code) return
		if (campaignActive || campaignPending) {
			setCouponError(
				'Other discounts cannot be combined with the Black Friday offer.'
			)
			return
		}
		try {
			const discount = await billingStore.fetchCoupon(code, {
				billing_period: cycle,
				interval: PERIOD_TO_INTERVAL[cycle],
				interval_count: PERIOD_TO_COUNT[cycle],
				account_id: account?.id
			})
			setCoupon(discount)
			setCouponCode(code)
			setCouponError(undefined)
		} catch (err: any) {
			setCoupon(null)
			setCouponError(couponErrorMessage(err))
		}
	}

	const isReady = () => isCustom || !!selectedPrice

	const handleConfirm = async (scheduleSubscriptionChange: boolean) => {
		if (isCustom || !selectedPlan || !selectedPrice || isBusy) return

		trackPostHogEvent(PostHogEvent.UPGRADE_CONFIRMATION, path, null)

		const outcome = await changePlan({
			accountId: account!.id,
			tier: selectedPlan.tier,
			priceId: selectedPrice.price_id,
			couponCode: coupon ? couponCode : null,
			scheduleSubscriptionChange
		})

		if (outcome.status === 'succeeded' || outcome.status === 'processing') {
			onClose()
		}
	}

	const handleQuote = async () => {
		await billingStore.createOfferRequest({
			account_id: account!.id,
			spend: Number(customSpend) || 0,
			interval: cycle,
			notes: 'From new subscription dialog'
		})
		onClose()
	}

	const uiPlans: ChangePlanPlan[] = useMemo(() => {
		const tierPlans = buildTierPlans({
			plans,
			selectedPlanId,
			selectedPrice,
			cycle,
			coupon
		})
		if (campaignActive && campaignQuote && cycle === 'yearly') {
			const currency = campaignQuote.currency as 'usd' | 'eur' | 'gbp'
			const money = (amount: number) =>
				new Intl.NumberFormat(undefined, {
					style: 'currency',
					currency: campaignQuote.currency.toUpperCase()
				}).format(amount / 100)
			for (const row of tierPlans) {
				const tier = plans.find((plan) => plan.tier === row.id)
				const annual =
					row.id === selectedPlanId
						? selectedPrice
						: tier && getBasePrice(tier, cycle)
				const monthly = tier?.prices.find(
					(price) =>
						price.interval === 'month' &&
						price.interval_count === 1 &&
						price.spend === annual?.spend
				)
				if (!annual || !monthly) continue
				const amount = Math.round(
					monthly.pricing_by_currency[currency].price * 12 * 0.65
				)
				row.originalPrice = `${money(
					annual.pricing_by_currency[currency].price / 12
				)} /month`
				row.originalPriceCaption = `${money(
					annual.pricing_by_currency[currency].price
				)} /year`
				row.price = `${money(amount / 12)} /month`
				row.priceCaption = `${money(
					amount
				)} /year · 35% off through ${discountEnd?.format('LL')}`
			}
		}

		tierPlans.push({
			id: 'custom',
			name: 'Custom',
			description: 'Request a custom plan',
			price: 'Custom pricing',
			priceCaption: 'Personalized quote',
			features: FEATURES.custom,
			customAdSpend: true,
			actions: {
				primaryAction: {
					label: 'Request quote',
					caption: 'Our team will send you an offer shortly',
					onClick: handleQuote,
					disabled: isBusy
				}
			}
		})

		return tierPlans
	}, [
		plans,
		selectedPlanId,
		selectedPriceId,
		cycle,
		coupon,
		isBusy,
		customSpend,
		campaignActive,
		campaignQuote
	])

	const adSpendOptions =
		selectedPlan && !isCustom
			? getPrices(selectedPlan, cycle).map((p) => ({
					value: p.price_id,
					label: formatCurrency(p.spend, 'dollar')
			  }))
			: undefined

	return (
		<>
			<style>{configFieldsFixCss}</style>
			<ChangePlanModal
				className={CONFIG_FIELDS_FIX}
				open={open}
				onClose={() => !isBusy && onClose()}
				closeOnOverlayClick={!isBusy}
				closeOnEscape={!isBusy}
				title={
					type === 'TRIAL'
						? 'Select subscription plan'
						: 'Change subscription plan'
				}
				cycles={CYCLES}
				cycle={cycle}
				onCycleChange={(c) => changeCycle(c as BillingPeriod)}
				plans={uiPlans}
				selectedPlanId={selectedPlanId ?? undefined}
				onSelectPlan={selectPlan}
				adSpendOptions={adSpendOptions}
				adSpendValue={selectedPriceId ?? undefined}
				onAdSpendChange={setSelectedPriceId}
				customAdSpendValue={customSpend}
				onCustomAdSpendChange={setCustomSpend}
				onApplyCoupon={claimCoupon}
				couponError={couponError}
				appliedCoupon={coupon ? couponCode : undefined}
				onRemoveCoupon={() => {
					setCoupon(null)
					setCouponError(undefined)
				}}
				secondaryAction={{
					label: 'Update on next billing date',
					caption: campaignActive
						? `Standard pricing starts on ${billingDate}`
						: `Will start on ${billingDate}`,
					onClick: () => handleConfirm(true),
					disabled: !isReady() || isBusy || campaignLoading || campaignPending
				}}
				primaryAction={{
					label: submitLabel,
					caption: campaignPending
						? `Your Black Friday annual plan starts ${billingDate}`
						: campaignActive && cycle === 'yearly'
						? `Upgrade is prorated; 35% off through ${discountEnd?.format(
								'LL'
						  )}`
						: type === 'TRIAL'
						? `New plan starts ${billingDate}`
						: 'New subscription will start today',
					onClick: () => handleConfirm(false),
					disabled:
						!isReady() ||
						isBusy ||
						campaignLoading ||
						campaignPending ||
						(campaignActive && cycle !== 'yearly')
				}}
			/>
		</>
	)
}
